import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "../api/client";

interface SignDayDetail {
  id: number;
  forecastDate: string;
  signCode: string;
  toneProfile: string;
  dayArchetype: string;
  overallSummary: string;
  generalHoroscope: string;
  loveHoroscope: string;
  financialHoroscope: string;
  healthHoroscope: string;
  careerHoroscope: string;
  familyHoroscope: string;
  friendHoroscope: string;
  flirtHoroscope: string;
  luckyFocus: string;
  cautionFocus: string;
  bestTimeHint: string;
  compatibilityTeaser: string;
  moodVectorJson: string;
  forecastVariantsJson: string;
}

const SIGN_NAMES: Record<string, string> = {
  aries: "Овен", taurus: "Телец", gemini: "Близнецы", cancer: "Рак",
  leo: "Лев", virgo: "Дева", libra: "Весы", scorpio: "Скорпион",
  sagittarius: "Стрелец", capricorn: "Козерог", aquarius: "Водолей", pisces: "Рыбы",
};

const SECTIONS: Array<[keyof SignDayDetail, string]> = [
  ["generalHoroscope", "Общий"],
  ["loveHoroscope", "Любовь"],
  ["financialHoroscope", "Финансы"],
  ["careerHoroscope", "Карьера"],
  ["healthHoroscope", "Здоровье"],
  ["familyHoroscope", "Семья"],
  ["friendHoroscope", "Друзья"],
  ["flirtHoroscope", "Флирт"],
];

export function SignDetail() {
  const { date, sign } = useParams<{ date: string; sign: string }>();

  // Endpoint /api/sign-day/:date/:sign не реализован в worker'е пока. Сделаем TODO.
  const data = useQuery({
    queryKey: ["sign-day", date, sign],
    queryFn: () => api<SignDayDetail>(`/api/sign-day?date=${date}&sign=${sign}`),
    retry: false,
  });

  if (!date || !sign) return <div>Bad params</div>;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl font-bold">{SIGN_NAMES[sign] ?? sign}</h1>
        <p className="text-white/50 text-sm mt-1">{date}</p>
      </div>

      {data.isError && (
        <div className="card p-6 bg-amber-500/5 border-amber-500/20 text-amber-200 text-sm">
          Endpoint /api/sign-day пока не реализован. Это TODO в worker'е.
        </div>
      )}

      {data.data && (
        <>
          <section className="card p-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <Field label="Tone profile" value={data.data.toneProfile} />
              <Field label="Day archetype" value={data.data.dayArchetype} />
              <Field label="Lucky focus" value={data.data.luckyFocus} />
              <Field label="Caution focus" value={data.data.cautionFocus} />
            </div>
          </section>

          <section className="card p-6">
            <h2 className="font-display text-xl font-semibold mb-3">Сводка дня</h2>
            <p className="text-white/80 leading-relaxed">{data.data.overallSummary}</p>
          </section>

          <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {SECTIONS.map(([key, title]) => {
              const text = (data.data as any)[key] as string;
              if (!text || text === "—") return null;
              return (
                <div key={key} className="card p-5">
                  <h3 className="font-display text-base font-semibold text-astro-violet mb-2">{title}</h3>
                  <p className="text-sm text-white/80 leading-relaxed">{text}</p>
                </div>
              );
            })}
          </section>
        </>
      )}
    </div>
  );
}

function Field({ label, value }: { label: string; value?: string | null }) {
  return (
    <div>
      <div className="text-white/40 text-xs uppercase">{label}</div>
      <div className="font-medium mt-1">{value || "—"}</div>
    </div>
  );
}
